import type {
  HospitalPatient,
  LatLng,
  Mission,
  Station,
  Vehicle,
  VehicleStatus,
  VehicleType,
} from './types';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { getPatientCapacity } from '@/data/stations';
import { haversineKm } from '@/lib/geo';
import {
  type Hydrant,
  findNearestHydrantInRange,
} from '@/data/hydrants';
import { WATER_MAX_HOSE_DISTANCE_M } from '@/data/resources';

/**
 * Pure helpers for the vehicle status machine.
 * No store access, no side effects — easy to unit-test.
 */

/** Status 8 (handover at hospital) duration in gameTime ms. 15 s gerafft. */
export const HANDOVER_DURATION_MS = 15_000;

/** How often to re-check hospital availability for parked Status-7 vehicles. */
export const HOSPITAL_RECHECK_INTERVAL_MS = 30_000;

/** Vehicle types that can transport patients. */
export const TRANSPORT_TYPES: ReadonlySet<VehicleType> = new Set(['RTW', 'KTW']);

export function isTransportVehicle(type: VehicleType): boolean {
  return TRANSPORT_TYPES.has(type);
}

/** "fire vehicle with water tank" predicate (active firefighter for water-drain). */
export function isWaterPumper(v: Vehicle): boolean {
  return VEHICLE_SPECS[v.type].category === 'fire' && v.waterTankMax > 0;
}

/** Vehicle carries breathing-air supply (only GW in our spec). */
export function isAirSupplyVehicle(v: Vehicle): boolean {
  return v.breathingAirMax > 0;
}

/** Whether a vehicle is currently in a transit status that needs a route. */
export function isTransitStatus(status: VehicleStatus): boolean {
  return status === 1 || status === 3 || status === 7;
}

/**
 * Pick the nearest hospital with at least one free patient slot.
 * Returns null if every hospital is full or the list is empty.
 */
export function pickNearestAvailableHospital(
  hospitals: Station[],
  from: LatLng,
  gameTime: number,
): Station | null {
  const candidates = hospitals
    .filter((h) => h.kind === 'hospital')
    .filter((h) => {
      // Skip hospitals still under construction
      if (h.buildCompleteAt && gameTime < h.buildCompleteAt) return false;
      const capacity = getPatientCapacity(h.kind, h.level);
      const occupied = (h.currentPatients ?? []).filter((p) => p.freeAt > gameTime).length;
      return occupied < capacity;
    })
    .map((h) => ({ hospital: h, distKm: haversineKm(from, h.position) }));

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.distKm - b.distKm);
  return candidates[0].hospital;
}

/**
 * Free up expired patient slots. Returns a fresh list with patients whose
 * `freeAt` is still in the future. Used by the tick to age out hospital state.
 */
export function pruneExpiredPatients(
  patients: HospitalPatient[] | undefined,
  gameTime: number,
): HospitalPatient[] {
  if (!patients || patients.length === 0) return [];
  return patients.filter((p) => p.freeAt > gameTime);
}

export interface DrainResult {
  /** Updated participants with reduced tanks (pure copy). */
  vehicles: Vehicle[];
  /** True iff all relevant vehicles are exhausted → mission stalls. */
  stalled: boolean;
  /** Vehicle ids that just crossed the 25 % breathing-air threshold this tick. */
  airWarnings: string[];
  /**
   * Map of vehicle.id → connected hydrant id, for those that drew water from a
   * hydrant this tick (used by the renderer to draw connection lines).
   */
  hydrantConnections: Map<string, string>;
}

/** L/min consumed at the standard pumping rate (Block B Wiki). */
const WATER_DRAIN_LPM = 200;
/** L/min consumed by a Gerätewagen-Atemschutz supply during AGT operations. */
const AIR_DRAIN_LPM = 200;
/** Per-vehicle warn threshold (% of full tank) for breathing air. */
const AIR_WARN_FRACTION = 0.25;

/**
 * Drain water from each water-pumping vehicle and breathing air from each
 * air-supply vehicle. Returns updated copies + a stalled flag.
 *
 * Stall logic:
 *  - If the mission requires water and **all** water-pumping participants
 *    have an empty tank, the mission stalls.
 *  - If the mission requires AGT supply and **all** air-supply participants
 *    have an empty tank, the mission stalls.
 *  - If the mission requires water/air but no participant carries any tank
 *    capacity to begin with, the mission does NOT stall (personal AGT bottles
 *    on crews are assumed to last the operation; explicit-supply vehicles
 *    only matter when present).
 */
export function applyResourceDrain(
  participants: Vehicle[],
  mission: Mission,
  deltaGameMs: number,
  hydrants: Hydrant[] = [],
  waterFeatures: ReadonlyArray<{ position: { lat: number; lng: number } }> = [],
): DrainResult {
  const dtSec = deltaGameMs / 1000;
  let stalled = false;
  const airWarnings: string[] = [];
  const hydrantConnections = new Map<string, string>();

  // 1. Water drain (only when mission requires it and is a fire mission)
  let updated = participants;
  if (
    (mission.waterRequiredLiters ?? 0) > 0 &&
    mission.category === 'fire'
  ) {
    const pumpers = participants.filter(isWaterPumper);
    if (pumpers.length > 0) {
      // Resolve hydrant connections per pumper (Phase 6.3).
      // A vehicle within WATER_MAX_HOSE_DISTANCE_M of a hydrant is fed by it
      // and does NOT drain its own tank.
      // Phase 13.7 — Vehicles with the open-water-pump module within 500 m of
      // a water feature also count as "supplied", same as a hydrant connection.
      const tankPumpers: Vehicle[] = [];
      for (const v of pumpers) {
        const hydrant = findNearestHydrantInRange(
          hydrants,
          v.position,
          WATER_MAX_HOSE_DISTANCE_M,
        );
        if (hydrant) {
          hydrantConnections.set(v.id, hydrant.id);
        } else if (vehicleHasOpenWaterSupply(v, waterFeatures)) {
          // Treated as supplied — no tank drain.
        } else {
          tankPumpers.push(v);
        }
      }
      // If every pumper has a hydrant, no tank drain at all and no stall.
      if (tankPumpers.length > 0) {
        const activeTankPumpers = tankPumpers.filter((v) => v.waterTankCurrent > 0);
        if (activeTankPumpers.length === 0) {
          stalled = true;
        } else {
          const drainPerSec = WATER_DRAIN_LPM / 60;
          const drainPerVehicle = drainPerSec * dtSec;
          const drainSet = new Set(activeTankPumpers.map((v) => v.id));
          updated = updated.map((v) =>
            drainSet.has(v.id)
              ? {
                  ...v,
                  waterTankCurrent: Math.max(0, v.waterTankCurrent - drainPerVehicle),
                }
              : v,
          );
        }
      }
    }
  }

  // 2. Breathing-air drain (only when mission requires AGT and an air-supply vehicle is present)
  if (mission.breathingAirRequired) {
    const supplyVehicles = updated.filter(isAirSupplyVehicle);
    if (supplyVehicles.length > 0) {
      const activeSupply = supplyVehicles.filter((v) => v.breathingAirCurrent > 0);
      if (activeSupply.length === 0) {
        stalled = true;
      } else {
        const drainPerSec = AIR_DRAIN_LPM / 60;
        const drainPerVehicle = drainPerSec * dtSec;
        updated = updated.map((v) => {
          if (!isAirSupplyVehicle(v) || v.breathingAirCurrent <= 0) return v;
          const before = v.breathingAirCurrent;
          const after = Math.max(0, before - drainPerVehicle);
          // Did we cross the warn threshold this tick?
          const warnCutoff = v.breathingAirMax * AIR_WARN_FRACTION;
          if (before > warnCutoff && after <= warnCutoff) {
            airWarnings.push(v.id);
          }
          return { ...v, breathingAirCurrent: after };
        });
      }
    }
  }

  return { vehicles: updated, stalled, airWarnings, hydrantConnections };
}

/**
 * Decide what a vehicle does after Status 4 work is complete.
 * Returns:
 *   - 'transport' if vehicle is a transport vehicle AND the mission still has
 *     undelivered patients to assign.
 *   - 'return' otherwise (Status 1 → Status 2).
 */
export function postSceneDecision(
  vehicle: Vehicle,
  mission: Mission,
  alreadyAssignedTransports: number,
): 'transport' | 'return' {
  const undelivered = mission.patientCount - mission.patientsDelivered - alreadyAssignedTransports;
  if (undelivered > 0 && isTransportVehicle(vehicle.type)) {
    return 'transport';
  }
  return 'return';
}

/**
 * Phase 13.7 — Does this vehicle have an open-water-pump module AND is it
 * within 500 m of a known water feature? Used by `applyResourceDrain` to
 * count it as continuously supplied.
 */
function vehicleHasOpenWaterSupply(
  v: Vehicle,
  features: ReadonlyArray<{ position: { lat: number; lng: number } }>,
): boolean {
  if (features.length === 0) return false;
  const installed = v.installedModules ?? [];
  const hasModule = installed.some((m) => m.moduleId.startsWith('open-water-pump'));
  if (!hasModule) return false;
  const cosLat = Math.cos((v.position.lat * Math.PI) / 180);
  const maxKm = 0.5; // 500 m
  for (const f of features) {
    const dLat = (f.position.lat - v.position.lat) * 111.32;
    const dLng = (f.position.lng - v.position.lng) * 111.32 * cosLat;
    const dKm = Math.sqrt(dLat * dLat + dLng * dLng);
    if (dKm <= maxKm) return true;
  }
  return false;
}
